<!--
  SettlementExportModal.vue
  콜봇 월 정산 · 정산 엑셀 내보내기 확인창 — 목록(SettlementList, 고른 병원들)과 상세(SettlementDetail, 그 병원 1곳)가
  같이 쓴다. 창은 부모가 가진다(open · close).

  설계: ~/md/plan/콜봇-월정산-정산시작일-엑셀내보내기-계획.md 4-2(화면·문구), 서버 계약:
  ~/md/back/콜봇-월정산-정산시작일-엑셀내보내기-call-api요청.md "요청 계약" 9절(POST /api/v1/callbot/usage/monthly/export).
  - 머리: '2026-10 · 병원 12곳', 처음 3곳 + '외 n곳', [모두 보기]로 전부 펼침. 범위 안내(정산 대상 아님 n곳은 사용량만,
    확정 전 병원은 저장된 값으로) — 문구와 셈은 settlementExportSelection.exportModalSummary.
  - 담을 내용: 정산서(기본 켬) · 원가·마진(대외비). 둘 다 끄면 막는다.
  - 내보내는 이유: 이용 내역의 EXPORT_REASONS·exportReasonError 를 그대로 쓴다(기타면 메모 필수). 본문은
    buildSettlementExportBody 가 정확히 month·company_ids·contents·reasonCode(·reasonNote)만 만든다
    (auditLog.buildExportBody 는 이용 내역 검색용이라 쓰지 않는다).
  - [내보내기] → 서버가 이용 내역을 남기고 회사별 정산을 돌려준다 → 시트 데이터(settlementExportRows) → 파일 쓰기.
    xlsx 는 무거워 파일 쓰기 모듈(@/utils/settlementExportWorkbook)을 누를 때만 불러온다(목록·상세 첫 화면에 싣지 않음).
  - 실패하면 창을 연 채(고른 내용·이유 그대로) 창 안에 해결 방법 문구를 보인다(exportErrorView — 오류 code 로 나눈다).
    파일은 만들지 않는다. 받은 뒤 파일 만들기에서 실패하면 이용 내역에는 남았다고 따로 알린다.
  - 누르는 동안(loading) 두 번 누르기·닫기를 막는다. 열릴 때마다 내용·이유·메모를 처음 값으로 돌린다.
  - 개인정보: 상담사 이름·번호 끝자리는 파일에 넣지 않는다(시트 모델 withNames:false — MASKING v1).

  - props: open · month('YYYY-MM') · companies([{ company_id, company_name, settlement_scope?, settlement_start_date? }]) · api
  - emits: close · done({ fileName, count })
-->
<template>
  <a-modal :open="open" :footer="null" :mask-closable="false" :closable="!loading" width="560px" @cancel="handleCancel">
    <template #title>
      <div class="modal-title">정산 엑셀 내보내기</div>
    </template>

    <div class="modal-content">
      <div class="export">
        <!-- 대상 -->
        <section class="export__target" aria-label="내보낼 병원">
          <p class="export__title">{{ summary.title }}</p>
          <p class="export__names">
            <span>{{ namesText }}</span>
            <a-button
              v-if="summary.moreCount > 0"
              type="link"
              size="small"
              class="export__more"
              :aria-expanded="showAll ? 'true' : 'false'"
              aria-controls="settlement-export-all-names"
              @click="showAll = !showAll"
            >
              {{ showAll ? '접기' : '모두 보기' }}
            </a-button>
          </p>
          <ul v-if="showAll" id="settlement-export-all-names" class="export__all" aria-label="고른 병원 전체">
            <li v-for="(name, i) in summary.allNames" :key="i">{{ name }}</li>
          </ul>
          <ul v-if="summary.notes.length" class="export__notes">
            <li v-for="(note, i) in summary.notes" :key="i">{{ note }}</li>
          </ul>
        </section>

        <!-- 담을 내용 -->
        <fieldset class="export__field">
          <legend class="export__label">담을 내용 <span class="export__hint">(하나 이상)</span></legend>
          <div class="export__options">
            <a-checkbox
              v-for="option in EXPORT_CONTENT_OPTIONS"
              :key="option.key"
              v-model:checked="contents[option.key]"
              class="export__option"
              :disabled="loading"
            >
              <span class="export__option-label">{{ option.label }}</span>
              <span class="export__option-sub">{{ option.sub }}</span>
              <span v-if="option.confidential" class="export__secret">대외비</span>
            </a-checkbox>
          </div>
        </fieldset>

        <!-- 이유 -->
        <div class="export__field">
          <span id="settlement-export-reason-label" class="export__label">내보내는 이유 <em>*</em></span>
          <a-select
            v-model:value="reasonCode"
            class="export__reason"
            :options="EXPORT_REASONS"
            :disabled="loading"
            placeholder="이유를 고르세요"
            aria-labelledby="settlement-export-reason-label"
          />
        </div>
        <label v-if="reasonCode === 'OTHER'" class="export__field">
          <span class="export__label">이유 메모 <em>*</em></span>
          <a-textarea
            v-model:value="reasonNote"
            :rows="2"
            :maxlength="REASON_NOTE_MAX"
            show-count
            :disabled="loading"
            placeholder="예: 11-02 재무팀 월 마감 자료 요청"
          />
        </label>

        <p class="export__audit">이 내보내기는 이용 내역에 남습니다.</p>

        <a-alert v-if="errorText" type="error" show-icon class="export__error" :message="errorText" />
      </div>

      <div class="modal-footer">
        <a-button :disabled="loading" @click="handleCancel">취소</a-button>
        <a-button type="primary" class="yellow-button" :loading="loading" @click="handleSubmit">내보내기</a-button>
      </div>
    </div>
  </a-modal>
</template>

<script setup>
import { computed, reactive, ref, watch } from 'vue';
import { message } from 'ant-design-vue';

import {
  DEFAULT_EXPORT_CONTENTS,
  EMPTY_SELECTION_TIP,
  EXPORT_CONTENT_OPTIONS,
  buildSettlementExportBody,
  exportContentsError,
  exportErrorView,
  exportModalSummary,
  exportSuccessText,
} from '../service/settlementExportSelection';
import { buildExportWorkbookData } from '../service/settlementExportRows';
import { EXPORT_REASONS, exportReasonError } from '@/utils/auditLog';

/** 이용 내역 화면(AuditLogManage)의 내려받기 메모와 같은 길이 */
const REASON_NOTE_MAX = 200;
/** 서버는 응답했고(이용 내역에 남음) 파일 만들기에서 실패 — 응답 오류 문구와 따로 둔다 */
const FILE_FAIL_TEXT =
  '받은 정산으로 파일을 만들지 못했습니다. 이용 내역에는 남았습니다. 다시 시도하거나 고르는 병원 수를 줄여 보세요.';

const props = defineProps({
  open: { type: Boolean, required: true },
  /** 'YYYY-MM'(KST) */
  month: { type: String, required: true },
  /** [{ company_id, company_name, settlement_scope?, settlement_start_date? }] — 목록 순서 그대로 */
  companies: { type: Array, required: true },
  /** settlementApi 또는 목(getSettlementApi()) — exportMonthlySettlement */
  api: { type: Object, required: true },
});

const emit = defineEmits(['close', 'done']);

const contents = reactive({ ...DEFAULT_EXPORT_CONTENTS });
const reasonCode = ref(undefined);
const reasonNote = ref('');
const showAll = ref(false);
const loading = ref(false);
const errorText = ref('');

function resetForm() {
  Object.assign(contents, DEFAULT_EXPORT_CONTENTS);
  reasonCode.value = undefined;
  reasonNote.value = '';
  showAll.value = false;
  errorText.value = '';
}

// 열릴 때마다 처음 값으로 — 지난번 고른 내용·이유가 남지 않게
watch(
  () => props.open,
  (open) => {
    if (open) resetForm();
  },
  { immediate: true }
);

const summary = computed(() => exportModalSummary(props.month, props.companies));

/** '가나치과, 다라의원, 마바병원 외 9곳' */
const namesText = computed(() => {
  const { names, moreCount } = summary.value;
  const head = names.join(', ');
  return moreCount > 0 ? `${head} 외 ${moreCount}곳` : head;
});

/** 지금 고른 내용(반응형 객체가 아닌 값) */
function contentsValue() {
  return { statement: contents.statement === true, cost_margin: contents.cost_margin === true };
}

function handleCancel() {
  if (loading.value) return;
  emit('close');
}

async function handleSubmit() {
  if (loading.value) return;

  const picked = contentsValue();
  const contentsError = exportContentsError(picked);
  if (contentsError) {
    message.warning(contentsError);
    return;
  }
  const note = reasonCode.value === 'OTHER' ? reasonNote.value : '';
  const reasonError = exportReasonError(reasonCode.value, note);
  if (reasonError) {
    message.warning(reasonError);
    return;
  }
  const companies = props.companies.filter((c) => c && c.company_id !== undefined && c.company_id !== null);
  if (!companies.length) {
    message.warning(EMPTY_SELECTION_TIP);
    return;
  }

  loading.value = true;
  errorText.value = '';
  try {
    let response;
    try {
      response = await props.api.exportMonthlySettlement(
        buildSettlementExportBody({
          month: props.month,
          companyIds: companies.map((c) => String(c.company_id)),
          contents: picked,
          reasonCode: reasonCode.value,
          reasonNote: note,
        })
      );
    } catch (err) {
      errorText.value = exportErrorView(err).text;
      return;
    }

    let fileName;
    try {
      const data = buildExportWorkbookData(response, { contents: picked, exportedAt: Date.now() });
      const workbook = await import('@/utils/settlementExportWorkbook');
      ({ fileName } = await workbook.writeSettlementWorkbook(data));
    } catch (err) {
      // 응답 모양 문제나 xlsx 불러오기 실패 — 콘솔에만 자세히(본문은 남기지 않는다)
      console.error('[settlement export] workbook failed:', err?.name ?? 'Error', err?.message ?? '');
      errorText.value = FILE_FAIL_TEXT;
      return;
    }

    message.success(exportSuccessText({ month: props.month, companies, contents: picked }));
    emit('done', { fileName, count: companies.length });
    emit('close');
  } finally {
    loading.value = false;
  }
}
</script>

<style scoped lang="scss">
@use '../styles/settlement' as *;

@include st-modal;
@include yellow-button;

.export {
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding-top: 4px;
  font-size: 13px;
  color: $st-text;

  p {
    margin: 0;
  }

  &__target {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  &__title {
    font-size: 14px;
    font-weight: 600;
  }

  &__names {
    color: $st-text-secondary;
    line-height: 1.6;
    word-break: keep-all;
  }

  // 전역 `button { height: 40px }` 를 덮어 글줄 높이에 맞춘다
  &__more {
    height: auto;
    padding: 0 4px;
  }

  &__all {
    max-height: 160px;
    margin: 0;
    padding: 6px 10px 6px 24px;
    overflow-y: auto;
    border: 1px solid $st-border;
    border-radius: 6px;
    background: $st-bg;
    color: $st-text-secondary;
    line-height: 1.7;
  }

  &__notes {
    margin: 4px 0 0;
    padding-left: 18px;
    color: $st-text-secondary;
    font-size: 12.5px;
    line-height: 1.6;
  }

  &__field {
    display: flex;
    flex-direction: column;
    gap: 6px;
    min-width: 0;
    margin: 0;
    padding: 0;
    border: 0;
  }

  &__label {
    padding: 0;
    color: $st-text;
    font-size: 13px;
    font-weight: 600;

    em {
      font-style: normal;
      color: $st-danger;
    }
  }

  &__hint {
    color: $st-muted;
    font-weight: 400;
  }

  &__options {
    display: flex;
    flex-direction: column;
    gap: 6px;
  }

  // antd 는 두 번째 체크박스부터 margin-inline-start 를 준다 — 세로로 쌓으므로 없앤다
  &__option {
    margin-inline-start: 0;
  }

  &__option-label {
    font-weight: 600;
  }

  &__option-sub {
    margin-left: 8px;
    color: $st-muted;
    font-size: 12.5px;
  }

  &__secret {
    @include st-pill($st-danger, $st-danger-bg, $st-danger-border);
    margin-left: 8px;
    vertical-align: 1px;
  }

  &__reason {
    width: 100%;
  }

  &__audit {
    color: $st-text-secondary;
    font-size: 12.5px;
  }

  &__error {
    margin: 0;
  }
}

// 체크박스 키보드 포커스
.export__option :deep(.ant-checkbox-input:focus-visible + .ant-checkbox-inner) {
  outline: 2px solid $st-accent;
  outline-offset: 2px;
}
</style>
